import { Fragment } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { IOrgIntegration } from "../../../../core/api/organizations/types";
import {
  Column,
  Row,
  Spacer,
  TwoColGrid,
} from "../../../../components/Layout/Layout";
import TextInput from "../../../../ui-lib/components/Inputs/TextInput";
import useTranslations from "../../../../core/i18n/useTranslations";
import { CustomSettingsOptionalFields } from "./CustomSettingsOptionalFields";
import Checkbox from "../../../../ui-lib/components/Checkbox/Checkbox";
import Button from "../../../../ui-lib/components/Button/Button";
import Dropdown from "../../../../ui-lib/components/Dropdown/Dropdown";

import styles from "./CustomSettings.module.css";

export const CustomSettings = () => {
  const t = useTranslations();
  const {
    formState: { errors },
    control,
    trigger,
  } = useFormContext<IOrgIntegration>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "expectedUserSettings",
  });

  return (
    <>
      {fields.map((setting, index) => (
        <Fragment key={`wrapper_${setting.id}`}>
          <div key={setting.id} className={styles.customSettingsContainer}>
            <Column
              type="top"
              align="fill"
              className={styles.customSettingsFields}
            >
              <Row align="start">
                <Controller
                  control={control}
                  name={`expectedUserSettings.${index}.parameterName`}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t("Organizations:custom_setting_name_label")}
                      placeholder={t(
                        "Organizations:custom_setting_name_placehoder"
                      )}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        trigger(`expectedUserSettings.${index}.parameterName`);
                      }}
                      validationError={
                        errors.expectedUserSettings?.[index]?.parameterName &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
                <Spacer size={32} />
                <Controller
                  control={control}
                  name={`expectedUserSettings.${index}.displayName`}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t("Organizations:displayName")}
                      placeholder={t("Organizations:displayName_placeholder")}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        trigger(`expectedUserSettings.${index}.displayName`);
                      }}
                      validationError={
                        errors.expectedUserSettings?.[index]?.displayName &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              </Row>
              <Spacer size={16} />
              <TwoColGrid>
                <Controller
                  control={control}
                  name={`expectedUserSettings.${index}.settingType`}
                  render={({ field }) => {
                    const settingTypes = [
                      {
                        id: "Int",
                        name: "Number",
                        isSelected: field.value === "Int",
                      },
                      {
                        id: "Dynamic",
                        name: "Dynamic",
                        isSelected: field.value === "Dynamic",
                      },
                      {
                        id: "String",
                        name: "String",
                        isSelected: field.value === "String",
                      },
                      {
                        id: "Enum",
                        name: "Enum",
                        isSelected: field.value === "Enum",
                      },
                    ];
                    return (
                      <Dropdown
                        title={t("Organizations:setting_type_label")}
                        items={settingTypes}
                        selectedItem={settingTypes.find((i) => i.isSelected)}
                        onSelectItem={(i) => {
                          field.onChange(i.id as string);
                        }}
                      />
                    );
                  }}
                />
              </TwoColGrid>
              <Spacer size={16} />
              <CustomSettingsOptionalFields settingIndex={index} />
              <Spacer size={12} />
              <Controller
                control={control}
                name={`expectedUserSettings.${index}.required`}
                render={({ field }) => (
                  <Checkbox
                    style={{ paddingLeft: 0 }}
                    label={t("Common:required")}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      trigger(`expectedUserSettings.${index}.required`);
                    }}
                    htmlFor={`expectedUserSettings.${index}.required`}
                  />
                )}
              />
            </Column>
            <Spacer size={32} />
            <Column>
              <Button
                image="x"
                variant="secondary"
                onClick={() => {
                  remove(index);
                }}
                className={styles.removeCustomSettingBtn}
                customStyles={{
                  marginBottom: errors.expectedUserSettings?.[index] ? 20 : 0,
                }}
              />
            </Column>
          </div>
          <Spacer key={`Spacer_${setting.id}`} size={16} />
        </Fragment>
      ))}
      <Button
        text={t("Organizations:add_custom_setting")}
        image="plus"
        variant="secondary"
        onClick={async () => {
          await trigger([`expectedUserSettings.${fields.length - 1}`]);
          if (errors.expectedUserSettings) {
            return;
          } else {
            append({
              parameterName: "",
              displayName: "",
              settingType: "Int",
              required: false,
              minimum: undefined,
              maximum: undefined,
              regex: "",
              selectableEnums: [],
            });
          }
        }}
      />
    </>
  );
};
